import { useEffect, useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { Award } from 'lucide-react'
import { CommunityLayout, GovPageTitle, GovCard, GovEmptyState } from '@/components/community/CommunityLayout'
import { useCommunity } from '@/components/community/CommunityContext'
import { BadgeCard, SectionLoading, Avatar } from '@/components/community/ui'
import {
  listBadges, myBadges, communityBadges,
  type BadgeResponse, type MemberBadgeResponse,
} from '@/services/communityApi'

export default function CommunityRecognitionPage() {
  const { t } = useTranslation()
  const { community } = useCommunity()
  const [badges, setBadges] = useState<BadgeResponse[]>([])
  const [mine, setMine] = useState<MemberBadgeResponse[]>([])
  const [communityAwarded, setCommunityAwarded] = useState<MemberBadgeResponse[]>([])
  const [loading, setLoading] = useState(true)

  const load = useCallback(() => {
    if (!community) return
    setLoading(true)
    Promise.allSettled([listBadges(), myBadges(), communityBadges(community.id)]).then(([b, m, c]) => {
      if (b.status === 'fulfilled') setBadges(b.value)
      if (m.status === 'fulfilled') setMine(m.value)
      if (c.status === 'fulfilled') setCommunityAwarded(c.value)
      setLoading(false)
    })
  }, [community])

  useEffect(() => { load() }, [load])

  if (!community) return null
  const earnedKeys = new Set(mine.map((mb) => mb.badge.key))

  return (
    <CommunityLayout>
      <GovPageTitle title={t('community.recognition.title')} subtitle={t('community.recognition.subtitle')} icon={Award} />

      {/* My badges */}
      <h2 className="text-sm font-semibold text-gray-700 mb-3">{t('community.recognition.myBadges')}</h2>
      {loading ? <SectionLoading rows={2} /> : mine.length === 0 ? (
        <GovEmptyState icon="🏅" title={t('community.recognition.noBadges')} description={t('community.recognition.noBadgesHint')} />
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-8">
          {mine.map((mb) => <BadgeCard key={mb.id} badge={mb.badge} earned />)}
        </div>
      )}

      {/* All badges (catalog with earned flag) */}
      <h2 className="text-sm font-semibold text-gray-700 mb-3">{t('community.recognition.catalog')}</h2>
      {loading ? <SectionLoading rows={3} /> : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-8">
          {badges.map((b) => <BadgeCard key={b.id} badge={b} earned={earnedKeys.has(b.key)} />)}
        </div>
      )}

      {/* Community wall — who earned what (recognition only; badges carry no cash value) */}
      <h2 className="text-sm font-semibold text-gray-700 mb-3">{t('community.recognition.communityWall')}</h2>
      {loading ? <SectionLoading rows={2} /> : communityAwarded.length === 0 ? (
        <GovEmptyState icon="🎖️" title={t('community.recognition.noCommunityBadges')} />
      ) : (
        <GovCard>
          <ul className="divide-y divide-gray-100">
            {communityAwarded.map((mb) => (
              <li key={mb.id} className="flex items-center gap-3 py-3">
                <Avatar name={mb.user.fullName} image={mb.user.profileImage} size={36} />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-gray-900 truncate">{mb.user.fullName}</p>
                  <p className="text-xs text-gray-500">{mb.badge.name} · {mb.badge.icon}</p>
                </div>
                <span className="text-xs text-gray-400 shrink-0">{t('community.recognition.recognized')}</span>
              </li>
            ))}
          </ul>
        </GovCard>
      )}
    </CommunityLayout>
  )
}